import { useState } from 'react';
import { Switch } from 'antd';
import { BellOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, WarningOutlined } from '@ant-design/icons';

import { DataTable, MenuThaoTac, Tile } from '../../../../components/shared';
import { Badge, Button, Empty, Input, Select, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { KHONG_CO, soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiRpa } from '../chung';
import { BADGE_KENH, BADGE_KENH_CU, PHAM_VI_QUY_TAC } from '../../constants';
import { useProjects } from '../../hooks/useOpenCore';
import { useBatQuyTac, useKenhThongBao, useQuyTacThongBao, useXoaQuyTac } from '../../hooks/useThongBao';
import {
  coBoGui,
  dangBat,
  kenhCu,
  kenhGuiDuoc,
  KENH,
  laKeThua,
  locQuyTac,
  quyTacCuaProject,
  SU_KIEN,
  TEN_KENH,
} from '../../nghiep-vu/thong-bao';
import type { BoLocQuyTac } from '../../nghiep-vu/thong-bao';
import type { ProjectOpenRpa, QuyTacThongBao } from '../../types';
import { HopQuyTac } from './HopQuyTac';

const CO_TRANG = [10, 20, 50, 100];
const LOC_TRONG: BoLocQuyTac = { tim: '', event: '', kenh: '', trangThai: '' };

type Hop = { kieu: 'form'; goc: QuyTacThongBao | null } | { kieu: 'xoa'; goc: QuyTacThongBao } | null;

/**
 * Tab Cấu hình thông báo (`RpaNotificationsView` của native): quy tắc máy chủ dùng để báo kết quả lượt chạy. Chọn một
 * Project ở "Áp dụng cho" thì thấy quy tắc riêng của Project cùng quy tắc chung kế thừa; dòng kế thừa chỉ bật/tắt cho
 * riêng Project đó (`excludedProjects`), sửa ở chế độ Mặc định. Sửa theo `rpa:thong-bao:update`.
 */
export function TabThongBao({ suaDuoc, docProjectDuoc }: { suaDuoc: boolean; docProjectDuoc: boolean }) {
  const { t } = useT();
  const q = useQuyTacThongBao();
  const projects = useProjects(docProjectDuoc);
  const [project, setProject] = useState('');
  const dsProject = projects.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-content-secondary">{t('rpaThongBao.phamVi')}</span>
        <Select<string>
          value={project}
          onChange={setProject}
          aria-label={t('rpaThongBao.phamVi')}
          className="w-full sm:w-72"
          showSearch
          optionFilterProp="label"
          loading={docProjectDuoc && projects.isPending}
          disabled={!docProjectDuoc}
          options={[{ value: '', label: t('rpaThongBao.phamViMacDinh') }, ...dsProject.map((p) => ({ value: p.id, label: p.name }))]}
        />
      </div>
      {q.isPending ? (
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : q.isError ? (
        <Tile>
          <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
        </Tile>
      ) : (
        <NoiDung ds={q.data} project={project} projects={dsProject} suaDuoc={suaDuoc} docProjectDuoc={docProjectDuoc} />
      )}
    </div>
  );
}

function NoiDung({
  ds,
  project,
  projects,
  suaDuoc,
  docProjectDuoc,
}: {
  ds: QuyTacThongBao[];
  project: string;
  projects: ProjectOpenRpa[];
  suaDuoc: boolean;
  docProjectDuoc: boolean;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const kenh = useKenhThongBao();
  const bat = useBatQuyTac();
  const [loc, setLoc] = useState<BoLocQuyTac>(LOC_TRONG);
  const [hop, setHop] = useState<Hop>(null);

  const theoPhamVi = project ? quyTacCuaProject(ds, project) : ds;
  const hien = locQuyTac(theoPhamVi, loc, project);
  const tenProject = (id: string) => projects.find((p) => p.id.toUpperCase() === id.toUpperCase())?.name ?? id;

  const doiBat = (r: QuyTacThongBao, moi: boolean) => {
    const keThua = laKeThua(r, project);
    bat.mutate(
      { id: r.id, enabled: moi, project: keThua ? project : '' },
      {
        onSuccess: () =>
          toast.success(
            keThua
              ? t(moi ? 'rpaThongBao.projectDaBat' : 'rpaThongBao.projectDaTat')
              : t(moi ? 'rpaThongBao.daBat' : 'rpaThongBao.daTat'),
          ),
        onError: (e) => toast.error(dichLoi(e)),
      },
    );
  };

  const nutThem = suaDuoc ? (
    <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'form', goc: null })}>
      {t('rpaThongBao.them')}
    </Button>
  ) : null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          allowClear
          autoComplete="off"
          prefix={<SearchOutlined aria-hidden />}
          placeholder={t('rpaThongBao.tim')}
          aria-label={t('rpaThongBao.tim')}
          value={loc.tim}
          onChange={(e) => setLoc((c) => ({ ...c, tim: e.target.value }))}
          className="w-full sm:w-64"
        />
        <Select<string>
          value={loc.event}
          onChange={(event) => setLoc((c) => ({ ...c, event }))}
          aria-label={t('rpaThongBao.locSuKien')}
          className="w-full sm:w-56"
          options={[{ value: '', label: t('rpaThongBao.moiSuKien') }, ...SU_KIEN.map((e) => ({ value: e, label: t(`rpaThongBao.suKien.${e}`) }))]}
        />
        <Select<string>
          value={loc.kenh}
          onChange={(k) => setLoc((c) => ({ ...c, kenh: k }))}
          aria-label={t('rpaThongBao.locKenh')}
          className="w-full sm:w-44"
          options={[{ value: '', label: t('rpaThongBao.moiKenh') }, ...KENH.map((k) => ({ value: k, label: TEN_KENH[k] }))]}
        />
        <Select<string>
          value={loc.trangThai}
          onChange={(trangThai) => setLoc((c) => ({ ...c, trangThai }))}
          aria-label={t('rpaThongBao.locTrangThai')}
          className="w-full sm:w-40"
          options={[
            { value: '', label: t('rpaThongBao.moiTrangThai') },
            { value: 'bat', label: t('rpa.dangBat') },
            { value: 'tat', label: t('rpa.dangTat') },
          ]}
        />
        <span className="text-xs text-content-muted" role="status">
          {t('rpaThongBao.dem', { hien: soVN(hien.length, locale), tong: soVN(theoPhamVi.length, locale) })}
        </span>
        {nutThem && <div className="ml-auto">{nutThem}</div>}
      </div>
      <p className="text-xs text-content-muted">
        {project ? t('rpaThongBao.goiYProject', { project: tenProject(project) }) : t('rpaThongBao.goiYMacDinh')}
      </p>
      {kenh.isError && (
        <p className="text-xs text-warning-700" role="alert">
          {t('rpaThongBao.kenhLoi', { loi: dichLoi(kenh.error) })}
        </p>
      )}

      <Tile className="min-w-0">
        {theoPhamVi.length === 0 ? (
          <Empty
            icon={BellOutlined}
            title={project ? t('rpaThongBao.rongProject') : t('rpaThongBao.rong')}
            description={t('rpaThongBao.rongMoTa')}
            action={nutThem ?? undefined}
          />
        ) : hien.length === 0 ? (
          <Empty icon={SearchOutlined} title={t('rpaThongBao.rongLoc')} />
        ) : (
          <DataTable<QuyTacThongBao>
            caption={t('rpaCauHinh.tabThongBao')}
            dataSource={hien}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={
              hien.length > 20
                ? {
                    defaultPageSize: 20,
                    pageSizeOptions: CO_TRANG.map(String),
                    showSizeChanger: true,
                    showTotal: (tong, [tu, den]) =>
                      t('rpaThongBao.tongTrang', { tu: soVN(tu, locale), den: soVN(den, locale), tong: soVN(tong, locale) }),
                  }
                : false
            }
            columns={[
              { title: t('chung.stt'), key: 'stt', width: 56, render: (_, __, i) => <span className="tabular-nums">{i + 1}</span> },
              {
                title: t('rpaThongBao.cotTen'),
                key: 'name',
                render: (_, r) => <span className="font-medium text-content-primary">{r.name || KHONG_CO}</span>,
              },
              {
                title: t('rpaThongBao.cotSuKien'),
                key: 'event',
                render: (_, r) => (
                  <div className="whitespace-nowrap">
                    <div>{t(`rpaThongBao.suKien.${r.event}`)}</div>
                    {!coBoGui(r.event) && (
                      <div className="flex items-center gap-1 text-xs text-warning-700">
                        <WarningOutlined aria-hidden />
                        {t('rpaThongBao.chuaCoBoGui')}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                title: t('rpaThongBao.cotPhamVi'),
                key: 'scope',
                render: (_, r) => {
                  const ten = r.workflows.map((w) => w.name || w.id);
                  const pv = project ? PHAM_VI_QUY_TAC[laKeThua(r, project) ? 'keThua' : 'rieng'] : null;
                  return (
                    <div className="min-w-0 space-y-0.5">
                      <div>
                        {pv ? (
                          <Badge variant={pv.variant}>{t(pv.khoa)}</Badge>
                        ) : r.projectCode ? (
                          tenProject(r.projectCode)
                        ) : (
                          t('rpaThongBao.tatCaDuAn')
                        )}
                      </div>
                      <div className="max-w-xs truncate text-xs text-content-muted">
                        {ten.length === 0
                          ? t('rpaThongBao.moiWorkflow')
                          : ten.length === 1
                            ? t('rpaThongBao.workflowMot', { ten: ten[0] })
                            : t('rpaThongBao.workflowNhieu', { so: soVN(ten.length, locale), ten: ten[0] })}
                      </div>
                    </div>
                  );
                },
              },
              {
                title: t('rpaThongBao.cotKenh'),
                key: 'channels',
                render: (_, r) => {
                  const gui = kenhGuiDuoc(r);
                  const cu = kenhCu(r);
                  if (!gui.length && !cu.length) return <span className="text-content-muted">{KHONG_CO}</span>;
                  return (
                    <div className="flex flex-wrap gap-1">
                      {gui.map((k) => (
                        <Badge key={k} variant={BADGE_KENH}>
                          {TEN_KENH[k]}
                        </Badge>
                      ))}
                      {cu.map((k) => (
                        <Badge key={k} variant={BADGE_KENH_CU}>
                          {t('rpaThongBao.kenhCu', { kenh: k })}
                        </Badge>
                      ))}
                    </div>
                  );
                },
              },
              {
                title: t('rpaThongBao.cotBat'),
                key: 'enabled',
                render: (_, r) => {
                  const keThua = laKeThua(r, project);
                  const dang = dangBat(r, project);
                  // Quy tắc chung đã tắt thì Project không bật lại được từ đây.
                  const chan = keThua && !r.enabled;
                  if (!suaDuoc) return <span className="text-sm">{t(dang ? 'rpa.dangBat' : 'rpa.dangTat')}</span>;
                  return (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={dang}
                        disabled={chan || (bat.isPending && bat.variables?.id === r.id)}
                        aria-label={t('rpaThongBao.batCauHinh', { ten: r.name })}
                        onChange={(moi) => doiBat(r, moi)}
                      />
                      {chan && <span className="text-xs text-content-muted">{t('rpaThongBao.keThuaDangTat')}</span>}
                    </div>
                  );
                },
              },
              ...(suaDuoc
                ? [
                    {
                      title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                      key: 'thaoTac',
                      width: 72,
                      align: 'center' as const,
                      fixed: 'right' as const,
                      render: (_: unknown, r: QuyTacThongBao) =>
                        laKeThua(r, project) ? (
                          <span className="whitespace-nowrap text-xs text-content-muted">{t('rpaThongBao.suaOMacDinh')}</span>
                        ) : (
                          <MenuThaoTac
                            tenBanGhi={r.name}
                            muc={[
                              { khoa: 'sua', nhan: t('rpaThongBao.sua'), icon: <EditOutlined />, nhom: 'sua', onChon: () => setHop({ kieu: 'form', goc: r }) },
                              { khoa: 'xoa', nhan: t('rpaThongBao.xoa'), icon: <DeleteOutlined />, nhom: 'pha-huy', onChon: () => setHop({ kieu: 'xoa', goc: r }) },
                            ]}
                          />
                        ),
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Tile>

      {hop?.kieu === 'form' && (
        <HopQuyTac
          goc={hop.goc}
          projectMacDinh={project}
          projects={projects}
          docProjectDuoc={docProjectDuoc}
          kenh={kenh.data ?? []}
          onClose={() => setHop(null)}
        />
      )}
      {hop?.kieu === 'xoa' && <HopXoaQuyTac goc={hop.goc} onClose={() => setHop(null)} />}
    </>
  );
}

function HopXoaQuyTac({ goc, onClose }: { goc: QuyTacThongBao; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const xoa = useXoaQuyTac();
  return (
    <HopXacNhanThaoTac
      open
      nguyHiem
      tieuDe={t('rpaThongBao.xoaTieuDe', { ten: goc.name })}
      than={t('rpaThongBao.xoaThan')}
      nhanXacNhan={t('rpaThongBao.xoa')}
      dangChay={xoa.isPending}
      loi={xoa.error ? dichLoi(xoa.error) : null}
      onClose={onClose}
      onXacNhan={() =>
        xoa.mutate(goc.id, {
          onSuccess: () => {
            toast.success(t('rpaThongBao.daXoa'));
            onClose();
          },
        })
      }
    />
  );
}
